@if (entity(); as currentEntity) {
  <div class="header-row">
    <h1>{{ entityTitle() }} {{ 'resourceDetails.details' | t }}</h1>
    <a mat-stroked-button class="back-desktop" [routerLink]="['/resources', currentEntity.key]" queryParamsHandling="preserve">
      <mat-icon>arrow_back</mat-icon>
      {{ 'resourceDetails.backToList' | t }}
    </a>
    <a mat-icon-button class="back-mobile" [routerLink]="['/resources', currentEntity.key]" queryParamsHandling="preserve" [attr.aria-label]="'resourceDetails.backToList' | t">
      <mat-icon>arrow_back</mat-icon>
    </a>
  </div>

  @if (row()) {
    <div class="profile-layout">
      <mat-card class="profile-card">
        <mat-card-content>
          <div class="profile-head">
            <div class="avatar-wrap">
              @if (profileImageUrl()) {
                <img [src]="profileImageUrl()" alt="Profile photo" class="avatar-image" />
              } @else {
                <div class="avatar-fallback">{{ profileInitials() }}</div>
              }
            </div>
            <div class="profile-main">
              <h2>{{ profileName() }}</h2>
              <p>{{ profileSubtitle() }}</p>
              @if (entityKey() !== 'resources') {
                <p>{{ 'common.status' | t }}: {{ getValue(row()!, 'status') }}</p>
              }
            </div>
            @if (canEditCurrentEntity() || getEntityStatusActions().length > 0) {
              <div class="status-actions">
                @if (canEditCurrentEntity()) {
                  <button mat-stroked-button (click)="openEditCurrentEntity()">
                    <mat-icon>edit</mat-icon>
                    {{ 'common.edit' | t }}
                  </button>
                }
                @for (action of getEntityStatusActions(); track action.nextStatus) {
                  <button mat-stroked-button (click)="applyEntityStatusAction(action.nextStatus)">
                    <mat-icon>{{ action.icon }}</mat-icon>
                    {{ action.labelKey | t }}
                  </button>
                }
              </div>
            }
            @if (entityKey() === 'instructors') {
              <div class="status-actions">
                @if (canEnterUserAppContext()) {
                  <button mat-flat-button color="primary" (click)="loginAsInstructor()">
                    <mat-icon>login</mat-icon>
                    {{ 'resourceDetails.loginAsInstructor' | t }}
                  </button>
                }
                @if (canEditResources()) {
                  @if (canUseInstructorCalendar()) {
                    <button mat-stroked-button (click)="openInstructorAvailability()">
                      <mat-icon>calendar_month</mat-icon>
                      {{ 'availability.action' | t }}
                    </button>
                    <button mat-stroked-button (click)="openInstructorCalendar()">
                      <mat-icon>calendar_view_week</mat-icon>
                      {{ 'calendar.title' | t }}
                    </button>
                    <button mat-stroked-button (click)="openInstructorUnavailability()">
                      <mat-icon>event_busy</mat-icon>
                      {{ 'unavailability.action' | t }}
                    </button>
                  }
                  <button mat-stroked-button (click)="openEditInstructorRates()">
                    <mat-icon>payments</mat-icon>
                    {{ 'resourceDetails.editInstructorRates' | t }}
                  </button>
                }
                @if (canInviteCurrentInstructor()) {
                  <button mat-stroked-button (click)="sendInstructorInvite()" [disabled]="invitingInstructor()">
                    <mat-icon>outgoing_mail</mat-icon>
                    {{ 'invitationSettings.manualInvite' | t }}
                  </button>
                }
                @if (canAssignStudentRelations()) {
                  <button mat-stroked-button (click)="openAssignCourseToInstructorDialog()">
                    <mat-icon>assignment</mat-icon>
                    {{ 'resourceDetails.assignCourse' | t }}
                  </button>
                  <button mat-stroked-button (click)="openAssignResourceToInstructorDialog()">
                    <mat-icon>directions_car</mat-icon>
                    {{ 'resourceDetails.assignResource' | t }}
                  </button>
                }
              </div>
            }
            @if (entityKey() === 'resources' && canEditResources()) {
              <div class="status-actions">
                @if (canUseResourceCalendar()) {
                  <button mat-stroked-button (click)="openResourceCalendar()">
                    <mat-icon>calendar_view_week</mat-icon>
                    {{ 'calendar.title' | t }}
                  </button>
                  <button mat-stroked-button (click)="openResourceUnavailability()">
                    <mat-icon>event_busy</mat-icon>
                    {{ 'resourceUnavailability.action' | t }}
                  </button>
                }
                <button mat-stroked-button (click)="openAssignCourseToResourceDialog()">
                  <mat-icon>assignment</mat-icon>
                  {{ 'resourceDetails.assignCourse' | t }}
                </button>
              </div>
            }
            @if (entityKey() === 'students') {
              <div class="profile-actions">
                @if (canEnterUserAppContext()) {
                  <button mat-flat-button color="primary" (click)="loginAsStudent()">
                    <mat-icon>login</mat-icon>
                    {{ 'resourceDetails.loginAsStudent' | t }}
                  </button>
                }
                @if (canAddLesson()) {
                  <button mat-stroked-button (click)="openAddLessonForStudent()">
                    <mat-icon>event_available</mat-icon>
                    {{ 'lessonDialog.addLesson' | t }}
                  </button>
                }
                @if (canAddPayment()) {
                  <button mat-stroked-button (click)="openAddPaymentForStudent()">
                    <mat-icon>payments</mat-icon>
                    {{ 'paymentDialog.addPayment' | t }}
                  </button>
                }
                @if (canAssignStudentRelations()) {
                  <button mat-stroked-button (click)="openAssignCourseDialog()">
                    <mat-icon>assignment</mat-icon>
                    {{ 'resourceDetails.assignCourse' | t }}
                  </button>
                  <button mat-stroked-button (click)="openAssignInstructorDialog()">
                    <mat-icon>person_add</mat-icon>
                    {{ 'resourceDetails.assignInstructor' | t }}
                  </button>
                }
              </div>
            }
            @if (hasMobileProfileActions()) {
              <div class="mobile-actions-menu">
                <button mat-icon-button [matMenuTriggerFor]="profileActionsMenu" [attr.aria-label]="'common.actions' | t">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #profileActionsMenu="matMenu">
                  @if (canEditCurrentEntity()) {
                    <button mat-menu-item (click)="openEditCurrentEntity()">
                      <mat-icon>edit</mat-icon>
                      <span>{{ 'common.edit' | t }}</span>
                    </button>
                  }

                  @for (action of getEntityStatusActions(); track action.nextStatus) {
                    <button mat-menu-item (click)="applyEntityStatusAction(action.nextStatus)">
                      <mat-icon>{{ action.icon }}</mat-icon>
                      <span>{{ action.labelKey | t }}</span>
                    </button>
                  }

                  @if (entityKey() === 'students') {
                    @if (canEnterUserAppContext()) {
                      <button mat-menu-item (click)="loginAsStudent()">
                        <mat-icon>login</mat-icon>
                        <span>{{ 'resourceDetails.loginAsStudent' | t }}</span>
                      </button>
                    }
                    @if (canAddLesson()) {
                      <button mat-menu-item (click)="openAddLessonForStudent()">
                        <mat-icon>event_available</mat-icon>
                        <span>{{ 'lessonDialog.addLesson' | t }}</span>
                      </button>
                    }
                    @if (canAddPayment()) {
                      <button mat-menu-item (click)="openAddPaymentForStudent()">
                        <mat-icon>payments</mat-icon>
                        <span>{{ 'paymentDialog.addPayment' | t }}</span>
                      </button>
                    }
                    @if (canAssignStudentRelations()) {
                      <button mat-menu-item (click)="openAssignCourseDialog()">
                        <mat-icon>assignment</mat-icon>
                        <span>{{ 'resourceDetails.assignCourse' | t }}</span>
                      </button>
                      <button mat-menu-item (click)="openAssignInstructorDialog()">
                        <mat-icon>person_add</mat-icon>
                        <span>{{ 'resourceDetails.assignInstructor' | t }}</span>
                      </button>
                    }
                  }

                  @if (entityKey() === 'instructors') {
                    @if (canEnterUserAppContext()) {
                      <button mat-menu-item (click)="loginAsInstructor()">
                        <mat-icon>login</mat-icon>
                        <span>{{ 'resourceDetails.loginAsInstructor' | t }}</span>
                      </button>
                    }
                    @if (canEditResources()) {
                      @if (canUseInstructorCalendar()) {
                        <button mat-menu-item (click)="openInstructorAvailability()">
                          <mat-icon>calendar_month</mat-icon>
                          <span>{{ 'availability.action' | t }}</span>
                        </button>
                        <button mat-menu-item (click)="openInstructorCalendar()">
                          <mat-icon>calendar_view_week</mat-icon>
                          <span>{{ 'calendar.title' | t }}</span>
                        </button>
                        <button mat-menu-item (click)="openInstructorUnavailability()">
                          <mat-icon>event_busy</mat-icon>
                          <span>{{ 'unavailability.action' | t }}</span>
                        </button>
                      }
                      <button mat-menu-item (click)="openEditInstructorRates()">
                        <mat-icon>payments</mat-icon>
                        <span>{{ 'resourceDetails.editInstructorRates' | t }}</span>
                      </button>
                    }
                    @if (canInviteCurrentInstructor()) {
                      <button mat-menu-item (click)="sendInstructorInvite()" [disabled]="invitingInstructor()">
                        <mat-icon>outgoing_mail</mat-icon>
                        <span>{{ 'invitationSettings.manualInvite' | t }}</span>
                      </button>
                    }
                    @if (canAssignStudentRelations()) {
                      <button mat-menu-item (click)="openAssignCourseToInstructorDialog()">
                        <mat-icon>assignment</mat-icon>
                        <span>{{ 'resourceDetails.assignCourse' | t }}</span>
                      </button>
                      <button mat-menu-item (click)="openAssignResourceToInstructorDialog()">
                        <mat-icon>directions_car</mat-icon>
                        <span>{{ 'resourceDetails.assignResource' | t }}</span>
                      </button>
                    }
                  }

                  @if (entityKey() === 'resources' && canEditResources()) {
                    @if (canUseResourceCalendar()) {
                      <button mat-menu-item (click)="openResourceCalendar()">
                        <mat-icon>calendar_view_week</mat-icon>
                        <span>{{ 'calendar.title' | t }}</span>
                      </button>
                      <button mat-menu-item (click)="openResourceUnavailability()">
                        <mat-icon>event_busy</mat-icon>
                        <span>{{ 'resourceUnavailability.action' | t }}</span>
                      </button>
                    }
                    <button mat-menu-item (click)="openAssignCourseToResourceDialog()">
                      <mat-icon>assignment</mat-icon>
                      <span>{{ 'resourceDetails.assignCourse' | t }}</span>
                    </button>
                  }
                </mat-menu>
              </div>
            }
          </div>

          @if (entityKey() === 'students' && studentStats(); as stats) {
            <div class="kpi-grid">
              <div class="kpi-card">
                <div class="kpi-label">{{ 'common.remaining' | t }}</div>
                <div class="kpi-value">{{ formatMoney(stats.balance) }}</div>
                <div class="kpi-sub">{{ 'common.paid' | t }}: {{ formatMoney(stats.paid) }} / {{ formatMoney(stats.totalCourseValue) }}</div>
              </div>
              <div class="kpi-card">
                <div class="kpi-label">{{ 'resourceDetails.courseProgress' | t }}</div>
                <div class="kpi-value">{{ stats.practiceProgress }}%</div>
                <div class="kpi-sub">{{ 'common.practice' | t }}: {{ stats.practiceDone }} / {{ stats.practicePlanned }} {{ 'common.hoursShort' | t }}</div>
                <mat-progress-bar mode="determinate" [value]="stats.practiceProgress" />
                <div class="kpi-sub">{{ 'common.theory' | t }}: {{ stats.theoryDone }} / {{ stats.theoryPlanned }} {{ 'common.hoursShort' | t }} ({{ stats.theoryProgress }}%)</div>
                <mat-progress-bar mode="determinate" [value]="stats.theoryProgress" />
              </div>
            </div>
          }

          @if (entityKey() === 'instructors' && canSeeInstructorPayoutProjection() && instructorStats(); as stats) {
            <div class="kpi-grid">
              <div class="kpi-card">
                <div class="kpi-label">{{ 'resourceDetails.projectedSalaryMonth' | t }}</div>
                <div class="kpi-value">{{ formatMoney(stats.projectedSalary) }}</div>
                <div class="instructor-payout-planned">
                  <span>{{ 'instructor.overview.plannedHours' | t }}</span>
                  <strong>{{ stats.bookedHours }} h</strong>
                </div>
                <div class="instructor-payout-breakdown">
                  <div>
                    <span>{{ 'instructor.overview.completedLessons' | t }}</span>
                    <strong>{{ stats.completedHours }} h</strong>
                  </div>
                  <div>
                    <span>{{ 'instructor.overview.payableCancelledHours' | t }}</span>
                    <strong>{{ stats.payableCancelledHours }} h</strong>
                  </div>
                </div>
              </div>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-content>
          <div class="mobile-tabs-nav">
            <button mat-icon-button (click)="previousDetailsTab()" [disabled]="isFirstDetailsTab()" [attr.aria-label]="'common.previous' | t">
              <mat-icon>chevron_left</mat-icon>
            </button>
            <div class="mobile-tabs-labels">
              <span class="mobile-tab-label active">{{ activeDetailsTabLabel() }}</span>
              @if (nextDetailsTabLabel()) {
                <span class="mobile-tab-label">{{ nextDetailsTabLabel() }}</span>
              }
            </div>
            <button mat-icon-button (click)="nextDetailsTab()" [disabled]="isLastDetailsTab()" [attr.aria-label]="'common.next' | t">
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>
          <mat-tab-group class="details-tabs" [selectedIndex]="detailsTabIndex()" (selectedIndexChange)="onDetailsTabIndexChange($event)">
            <mat-tab [label]="'resourceDetails.overview' | t">
              @if (hasStructuredOverview()) {
                <div class="overview-sections">
                  @for (section of overviewSections(); track section.key) {
                    <section class="overview-section">
                      <h3>{{ section.label }}</h3>
                      <div class="fields-grid">
                        @for (field of section.fields; track field.key) {
                          <div class="field-item">
                            <div class="field-key">{{ field.label }}</div>
                            <div class="field-value">{{ field.value }}</div>
                          </div>
                        }
                      </div>
                    </section>
                  }
                </div>
              } @else {
                <div class="fields-grid">
                  @for (field of allFields(); track field.key) {
                    <div class="field-item">
                      <div class="field-key">{{ field.label }}</div>
                      <div class="field-value">{{ field.value }}</div>
                    </div>
                  }
                </div>
              }
            </mat-tab>

            @if (entityKey() === 'students') {
              <mat-tab [label]="'resourceDetails.lessons' | t">
                <div class="tab-table">
                  <div class="embedded-table-toolbar">
                    <label>{{ 'common.from' | t }} <input type="date" [value]="embeddedLessonsDateFrom()" (change)="setEmbeddedDateFilter('lessons', 'from', $any($event.target).value)" /></label>
                    <label>{{ 'common.to' | t }} <input type="date" [value]="embeddedLessonsDateTo()" (change)="setEmbeddedDateFilter('lessons', 'to', $any($event.target).value)" /></label>
                    <button mat-button (click)="clearEmbeddedFilters('lessons')">{{ 'common.clear' | t }}</button>
                  </div>
                  <table mat-table [dataSource]="studentLessons()" class="desktop-table">
                    @for (column of studentLessonColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="studentLessonColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: studentLessonColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="studentLessons()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentLessons', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('studentLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentLessons', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('studentLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator [length]="embeddedLessonsTotal()" [pageIndex]="embeddedLessonsPageIndex()" [pageSize]="embeddedLessonsPageSize()" [pageSizeOptions]="[5, 10, 20, 50]" (page)="onEmbeddedLessonsPageChange($event)" />
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.payments' | t">
                <div class="tab-table">
                  <div class="embedded-table-toolbar">
                    <label>{{ 'common.from' | t }} <input type="date" [value]="embeddedPaymentsDateFrom()" (change)="setEmbeddedDateFilter('payments', 'from', $any($event.target).value)" /></label>
                    <label>{{ 'common.to' | t }} <input type="date" [value]="embeddedPaymentsDateTo()" (change)="setEmbeddedDateFilter('payments', 'to', $any($event.target).value)" /></label>
                    <button mat-button (click)="clearEmbeddedFilters('payments')">{{ 'common.clear' | t }}</button>
                  </div>
                  <table mat-table [dataSource]="studentPayments()" class="desktop-table">
                    @for (column of studentPaymentColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'actions') {
                            <div class="row-actions">
                              @for (action of getStudentPaymentStatusActions(rowData); track action.nextStatus) {
                                <button mat-stroked-button [disabled]="paymentSettlementsPending().has('' + rowData['id'])" (click)="applyStudentPaymentStatusAction(rowData, action.nextStatus)">
                                  <mat-icon>{{ action.icon }}</mat-icon>
                                  {{ action.labelKey | t }}
                                </button>
                              } @empty {
                                <span class="no-actions">-</span>
                              }
                            </div>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="studentPaymentColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: studentPaymentColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="studentPayments()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentPayments', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('studentPayments', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentPayments', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('studentPayments', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        @for (action of getStudentPaymentStatusActions(rowData); track action.nextStatus) {
                          <button mat-stroked-button class="mobile-action-btn" [disabled]="paymentSettlementsPending().has('' + rowData['id'])" (click)="applyStudentPaymentStatusAction(rowData, action.nextStatus)">
                            <mat-icon>{{ action.icon }}</mat-icon>
                            {{ action.labelKey | t }}
                          </button>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator [length]="embeddedPaymentsTotal()" [pageIndex]="embeddedPaymentsPageIndex()" [pageSize]="embeddedPaymentsPageSize()" [pageSizeOptions]="[5, 10, 20, 50]" (page)="onEmbeddedPaymentsPageChange($event)" />
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.courses' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="studentCourses()" class="desktop-table">
                    @for (column of studentCourseColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getStudentCourseColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'actions') {
                            <div class="row-actions">
                              @if (canMoveStudentCourse(rowData)) {
                                <button mat-stroked-button (click)="openMoveStudentCourseDialog(rowData)">
                                  <mat-icon>swap_horiz</mat-icon>
                                  {{ 'studentCourse.move.action' | t }}
                                </button>
                              }
                              @for (action of getStudentCourseStatusActions(rowData); track action.nextStatus) {
                                <button mat-stroked-button (click)="applyStudentCourseStatusAction(rowData, action.nextStatus)">
                                  <mat-icon>{{ action.icon }}</mat-icon>
                                  {{ action.labelKey | t }}
                                </button>
                              }
                              <button mat-stroked-button (click)="confirmUnlinkRelation('studentCourse', rowData)">
                                <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                              </button>
                            </div>
                          } @else if (column === 'progress') {
                            <div class="course-progress-cell">
                              <div class="course-progress-label">{{ getCourseProgressLabel(rowData) }}</div>
                              <mat-progress-bar mode="determinate" [value]="getCourseProgressPercent(rowData)" />
                            </div>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="studentCourseColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: studentCourseColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="studentCourses()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentCourses', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('studentCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentCourses', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('studentCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        @for (action of getStudentCourseStatusActions(rowData); track action.nextStatus) {
                          <button mat-stroked-button class="mobile-action-btn" (click)="applyStudentCourseStatusAction(rowData, action.nextStatus)">
                            <mat-icon>{{ action.icon }}</mat-icon>
                            {{ action.labelKey | t }}
                          </button>
                        }
                        @if (canMoveStudentCourse(rowData)) {
                          <button mat-stroked-button class="mobile-action-btn" (click)="openMoveStudentCourseDialog(rowData)">
                            <mat-icon>swap_horiz</mat-icon>
                            {{ 'studentCourse.move.action' | t }}
                          </button>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('studentCourse', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.instructors' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="studentInstructors()" class="desktop-table">
                    @for (column of studentInstructorColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'relationActions') {
                            <button mat-icon-button (click)="confirmUnlinkRelation('studentInstructor', rowData)" [attr.aria-label]="'relationUnlink.action' | t">
                              <mat-icon>link_off</mat-icon>
                            </button>
                          } @else if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="studentInstructorColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: studentInstructorColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="studentInstructors()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentInstructors', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('studentInstructors', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentInstructors', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('studentInstructors', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('studentInstructor', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>
            }

            @if (entityKey() === 'instructors') {
              <mat-tab [label]="'resourceDetails.lessons' | t">
                <div class="tab-table">
                  <div class="lesson-filter-bar">
                    <button mat-stroked-button (click)="openInstructorLessonsFilterDialog()">
                      <mat-icon>filter_list</mat-icon>
                      {{ 'common.filter' | t }}
                    </button>
                    <button
                      mat-stroked-button
                      [class.quick-filter-active]="instructorLessonsQuickFilter() === 'completed'"
                      (click)="setInstructorLessonsQuickFilter('completed')"
                    >
                      {{ 'status.completed' | t }}
                    </button>
                    <button
                      mat-stroked-button
                      [class.quick-filter-active]="instructorLessonsQuickFilter() === 'planned'"
                      (click)="setInstructorLessonsQuickFilter('planned')"
                    >
                      {{ 'status.planned' | t }}
                    </button>
                    <button
                      mat-button
                      [disabled]="instructorLessonsQuickFilter() === 'all'"
                      (click)="setInstructorLessonsQuickFilter('all')"
                    >
                      {{ 'common.all' | t }}
                    </button>
                    <button mat-button (click)="clearInstructorLessonsFilter()" [disabled]="!hasInstructorLessonsFilter()">
                      {{ 'common.clear' | t }}
                    </button>
                    @if (hasInstructorLessonsFilter()) {
                      <div class="lesson-active-filter">
                        {{ 'common.active' | t }}: {{ instructorLessonsFilterSummary() }}
                      </div>
                    }
                    <div class="lesson-count-chip">
                      {{ 'resourceDetails.showing' | t }} {{ instructorLessonsPageRows().length }} {{ 'resourceDetails.of' | t }} {{ instructorLessonsTotalItems() }} ({{ 'resourceDetails.latestFirst' | t }})
                    </div>
                  </div>
                  <table mat-table [dataSource]="instructorLessonsPageRows()" class="desktop-table">
                    @for (column of instructorLessonColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="instructorLessonColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: instructorLessonColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="instructorLessonsPageRows()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorLessons', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('instructorLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorLessons', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('instructorLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator
                    [length]="instructorLessonsTotalItems()"
                    [pageSize]="instructorLessonsPageSize()"
                    [pageIndex]="instructorLessonsPageIndex()"
                    [pageSizeOptions]="[5, 10, 20]"
                    (page)="onInstructorLessonsPageChange($event)"
                  />
                </div>
              </mat-tab>

              <mat-tab [label]="'common.students' | t">
                <div class="tab-table">
                  <div class="embedded-table-toolbar instructor-students-toolbar">
                    <label>{{ 'common.search' | t }}
                      <input type="search" [value]="instructorStudentsQuery()" (input)="setInstructorStudentsQuery($any($event.target).value)" />
                    </label>
                    <button mat-stroked-button [class.quick-filter-active]="instructorStudentsStatus() === 'active'" (click)="setInstructorStudentsStatus('active')">{{ 'common.active' | t }}</button>
                    <button mat-stroked-button [class.quick-filter-active]="instructorStudentsStatus() === 'disabled'" (click)="setInstructorStudentsStatus('disabled')">{{ 'status.disabled' | t }}</button>
                    <button mat-button [class.quick-filter-active]="instructorStudentsStatus() === 'all'" (click)="setInstructorStudentsStatus('all')">{{ 'common.all' | t }}</button>
                    <button mat-button [disabled]="!instructorStudentsQuery() && instructorStudentsStatus() === 'all'" (click)="clearInstructorStudentsFilters()">{{ 'common.clear' | t }}</button>
                  </div>
                  <table mat-table [dataSource]="instructorStudents()" class="desktop-table">
                    @for (column of instructorStudentColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'relationActions') {
                            <button mat-icon-button (click)="confirmUnlinkRelation('instructorStudent', rowData)" [attr.aria-label]="'relationUnlink.action' | t">
                              <mat-icon>link_off</mat-icon>
                            </button>
                          } @else if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="instructorStudentColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: instructorStudentColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="instructorStudents()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorStudents', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('instructorStudents', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorStudents', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('instructorStudents', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('instructorStudent', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator
                    [length]="instructorStudentsTotalItems()"
                    [pageSize]="instructorStudentsPageSize()"
                    [pageIndex]="instructorStudentsPageIndex()"
                    [pageSizeOptions]="[5, 10, 20, 50]"
                    (page)="onInstructorStudentsPageChange($event)"
                  />
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.courses' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="instructorCourses()" class="desktop-table">
                    @for (column of instructorCourseColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'relationActions') {
                            <button mat-icon-button (click)="confirmUnlinkRelation('instructorCourse', rowData)" [attr.aria-label]="'relationUnlink.action' | t">
                              <mat-icon>link_off</mat-icon>
                            </button>
                          } @else if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="instructorCourseColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: instructorCourseColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="instructorCourses()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorCourses', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('instructorCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorCourses', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('instructorCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('instructorCourse', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>

              <mat-tab [label]="'entity.resources' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="instructorResources()" class="desktop-table">
                    @for (column of instructorResourceColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>
                          @if (column === 'defaultResource') {
                            {{ 'resourceDetails.defaultResource' | t }}
                          } @else if (column !== 'resourceActions') {
                            {{ getColumnLabel(column) }}
                          }
                        </th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'defaultResource') {
                            @if (isDefaultInstructorResource(rowData.id)) {
                              <span class="relation-badge">{{ 'resourceDetails.defaultResource' | t }}</span>
                            }
                          } @else if (column === 'resourceActions') {
                            <button mat-icon-button [matMenuTriggerFor]="resourceActions" [attr.aria-label]="'common.actions' | t">
                              <mat-icon>more_vert</mat-icon>
                            </button>
                            <mat-menu #resourceActions="matMenu">
                              <button mat-menu-item [disabled]="isDefaultInstructorResource(rowData.id)" (click)="setDefaultInstructorResource(rowData.id)">
                                <mat-icon>star</mat-icon>
                                <span>{{ 'resourceDetails.setDefaultResource' | t }}</span>
                              </button>
                              <button mat-menu-item (click)="unassignResourceFromInstructor(rowData.id)">
                                <mat-icon>link_off</mat-icon>
                                <span>{{ 'resourceDetails.unassignResource' | t }}</span>
                              </button>
                            </mat-menu>
                          } @else if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="instructorResourceColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: instructorResourceColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="instructorResources()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorResources', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('instructorResources', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorResources', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('instructorResources', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('instructorResource', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.settlements' | t">
                <div class="tab-table">
                  <table class="settlement-table">
                    <thead>
                      <tr>
                        <th></th>
                        @for (column of instructorSettlementColumns; track column) {
                          <th>{{ getColumnLabel(column) }}</th>
                        }
                        <th>{{ 'common.actions' | t }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (rowData of instructorSettlementRows(); track rowData.monthKey) {
                        <tr class="settlement-summary-row" (click)="toggleSettlementMonth(rowData)">
                          <td>
                            <button
                              mat-icon-button
                              type="button"
                              class="settlement-expand-button"
                              (click)="toggleSettlementMonth(rowData); $event.stopPropagation()"
                              [attr.aria-expanded]="isSettlementMonthExpanded(rowData)"
                            >
                              <mat-icon>{{ isSettlementMonthExpanded(rowData) ? 'expand_less' : 'expand_more' }}</mat-icon>
                            </button>
                          </td>
                          @for (column of instructorSettlementColumns; track column) {
                            <td>{{ getValue(rowData, column) }}</td>
                          }
                          <td class="settlement-actions">
                            @if (canSettleMonth(rowData)) {
                              <button
                                mat-stroked-button
                                type="button"
                                (click)="settleInstructorMonth(rowData, $event)"
                                [disabled]="settlingMonth() === rowData.monthKey"
                              >
                                @if (settlingMonth() === rowData.monthKey) {
                                  <mat-icon>hourglass_top</mat-icon>
                                } @else {
                                  <mat-icon>task_alt</mat-icon>
                                }
                                {{ 'settlement.settleMonth' | t }}
                              </button>
                            } @else if (rowData['hasSettlement']) {
                              <span class="settlement-badge">
                                <mat-icon>check_circle</mat-icon>
                                {{ 'settlement.settled' | t }}
                              </span>
                            } @else {
                              <span class="no-actions">-</span>
                            }
                          </td>
                        </tr>
                        @if (isSettlementMonthExpanded(rowData)) {
                          <tr class="settlement-detail-row">
                            <td [attr.colspan]="instructorSettlementColumns.length + 2">
                              <div class="settlement-detail">
                                @if (rowData['missingRateWarning']) {
                                  <div class="settlement-warning">
                                    <mat-icon>warning</mat-icon>
                                    {{ rowData['missingRateWarning'] }}
                                  </div>
                                }
                                <table>
                                  <thead>
                                    <tr>
                                      @for (column of instructorSettlementCategoryColumns; track column) {
                                        <th>{{ getColumnLabel(column) }}</th>
                                      }
                                    </tr>
                                  </thead>
                                  <tbody>
                                    @for (categoryRow of settlementCategoryRows(rowData); track categoryRow['categoryName']) {
                                      <tr>
                                        @for (column of instructorSettlementCategoryColumns; track column) {
                                          <td>{{ getValue(categoryRow, column) }}</td>
                                        }
                                      </tr>
                                    }
                                  </tbody>
                                </table>
                                @if (settlementPaymentRows(rowData).length > 0) {
                                  <h3 class="settlement-detail-title">{{ 'settlement.paymentsCollected' | t }}</h3>
                                  <table>
                                    <thead>
                                      <tr>
                                        @for (column of instructorSettlementPaymentColumns; track column) {
                                          <th>{{ getColumnLabel(column) }}</th>
                                        }
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (paymentRow of settlementPaymentRows(rowData); track paymentRow['paymentId']) {
                                        <tr>
                                          @for (column of instructorSettlementPaymentColumns; track column) {
                                            <td>{{ getValue(paymentRow, column) }}</td>
                                          }
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                }
                              </div>
                            </td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </mat-tab>

              <mat-tab [label]="'resourceDetails.rateHistory' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="instructorRateHistory()" class="desktop-table">
                    @for (column of instructorRateHistoryColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">{{ getValue(rowData, column) }}</td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="instructorRateHistoryColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: instructorRateHistoryColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="instructorRateHistory()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorRateHistory', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('instructorRateHistory', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('instructorRateHistory', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('instructorRateHistory', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>

            }

            @if (entityKey() === 'resources') {
              <mat-tab [label]="'resourceDetails.courses' | t">
                <div class="tab-table">
                  <table mat-table [dataSource]="resourceCourses()" class="desktop-table">
                    @for (column of resourceCourseColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (column === 'relationActions') {
                            <button mat-icon-button (click)="confirmUnlinkRelation('resourceCourse', rowData)" [attr.aria-label]="'relationUnlink.action' | t">
                              <mat-icon>link_off</mat-icon>
                            </button>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="resourceCourseColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: resourceCourseColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="resourceCourses()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentCourses', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('studentCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('studentCourses', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('studentCourses', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                        <button mat-stroked-button class="mobile-action-btn" (click)="confirmUnlinkRelation('resourceCourse', rowData)">
                          <mat-icon>link_off</mat-icon>{{ 'relationUnlink.action' | t }}
                        </button>
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                </div>
              </mat-tab>
            }

            @if (entityKey() === 'resources' || entityKey() === 'courseInstances') {
              <mat-tab [label]="'resourceDetails.lessons' | t">
                <div class="tab-table">
                  <div class="embedded-table-toolbar">
                    <label>{{ 'common.from' | t }} <input type="date" [value]="embeddedLessonsDateFrom()" (change)="setEmbeddedDateFilter('lessons', 'from', $any($event.target).value)" /></label>
                    <label>{{ 'common.to' | t }} <input type="date" [value]="embeddedLessonsDateTo()" (change)="setEmbeddedDateFilter('lessons', 'to', $any($event.target).value)" /></label>
                    <button mat-button (click)="clearEmbeddedFilters('lessons')">{{ 'common.clear' | t }}</button>
                  </div>
                  <table mat-table [dataSource]="relatedLessons()" class="desktop-table">
                    @for (column of genericLessonColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="genericLessonColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: genericLessonColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="relatedLessons()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('genericLessons', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('genericLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('genericLessons', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('genericLessons', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator [length]="embeddedLessonsTotal()" [pageIndex]="embeddedLessonsPageIndex()" [pageSize]="embeddedLessonsPageSize()" [pageSizeOptions]="[5, 10, 20, 50]" (page)="onEmbeddedLessonsPageChange($event)" />
                </div>
              </mat-tab>
            }

            @if (entityKey() === 'courseInstances') {
              <mat-tab [label]="'resourceDetails.payments' | t">
                <div class="tab-table">
                  <div class="embedded-table-toolbar">
                    <label>{{ 'common.from' | t }} <input type="date" [value]="embeddedPaymentsDateFrom()" (change)="setEmbeddedDateFilter('payments', 'from', $any($event.target).value)" /></label>
                    <label>{{ 'common.to' | t }} <input type="date" [value]="embeddedPaymentsDateTo()" (change)="setEmbeddedDateFilter('payments', 'to', $any($event.target).value)" /></label>
                    <button mat-button (click)="clearEmbeddedFilters('payments')">{{ 'common.clear' | t }}</button>
                  </div>
                  <table mat-table [dataSource]="relatedPayments()" class="desktop-table">
                    @for (column of genericPaymentColumns; track column) {
                      <ng-container [matColumnDef]="column">
                        <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column) }}</th>
                        <td mat-cell *matCellDef="let rowData">
                          @if (canLinkToDetails(rowData, column)) {
                            <a class="cell-link" href="" (click)="openLinkedDetails(rowData, column, $event)">{{ getValue(rowData, column) }}</a>
                          } @else {
                            {{ getValue(rowData, column) }}
                          }
                        </td>
                      </ng-container>
                    }
                    <tr mat-header-row *matHeaderRowDef="genericPaymentColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: genericPaymentColumns"></tr>
                  </table>
                  <table mat-table [dataSource]="relatedPayments()" class="mobile-table">
                    <ng-container matColumnDef="mobileLeft">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('genericPayments', 'mobileLeft') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileLeftLines('genericPayments', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <ng-container matColumnDef="mobileRight">
                      <th mat-header-cell *matHeaderCellDef>{{ getMobileHeader('genericPayments', 'mobileRight') }}</th>
                      <td mat-cell *matCellDef="let rowData">
                        @for (line of getMobileRightLines('genericPayments', rowData); track line) {
                          <div class="mobile-line">{{ line }}</div>
                        }
                      </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="mobileColumns"></tr>
                    <tr mat-row *matRowDef="let rowData; columns: mobileColumns"></tr>
                  </table>
                  <mat-paginator [length]="embeddedPaymentsTotal()" [pageIndex]="embeddedPaymentsPageIndex()" [pageSize]="embeddedPaymentsPageSize()" [pageSizeOptions]="[5, 10, 20, 50]" (page)="onEmbeddedPaymentsPageChange($event)" />
                </div>
              </mat-tab>
            }
          </mat-tab-group>
        </mat-card-content>
      </mat-card>
    </div>
  } @else {
    <mat-card>
      <mat-card-content>{{ 'resourceDetails.notFound' | t }}</mat-card-content>
    </mat-card>
  }
}

